import { IProjectQueryParam, ProjectForecast } from "models/project";
import { Dispatch } from "redux";
import { setProjectCount, setProjects } from "./reducer";
import { toast } from "react-toastify";
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar";
import Department from "models/department";

export const getProjectForecast = (query?: IProjectQueryParam) => async (dispatch: Dispatch) => {
    try {
        let queryString = '';
        if (query) {
            const params = new URLSearchParams();

            Object.entries(query).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;

                if (Array.isArray(value)) {
                    value.forEach(v => {
                        if (v) params.append(key, v);
                    });
                } else {
                    params.append(key, String(value));
                }
            });

            queryString = params.toString();
        }

        const url = `${process.env.REACT_APP_API_URL}/project/project-finance${queryString ? `?${queryString}` : ''}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        dispatch(setProjects(data.data.data));
        dispatch(setProjectCount(data.data.count));

    } catch (err: any) {
        toast.error(err.message ?? "Error getting projects");
    }
}



export const getProjectForecastSearchFields = async () => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/project/search-fields`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting project forecast search fields");
        return null;
    }
}

export const downloadProjectForecastCSV = (
    projects: ProjectForecast[],
    fiscalYear: number,
    query: IProjectQueryParam,
    departments: Department[]
) => {
    if (!projects || projects.length === 0) return;
    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

    // NOTE: Branch/Project Manager name lookups are unavailable until the shared
    // Branch/Employee models are migrated (deferred, separate from this pass) — shown
    // as raw IDs for now.
    const branchName = query.branchIds?.length ? query.branchIds.join(", ") : "All"
    const pmNames = query.projectManagerIds?.length ? query.projectManagerIds.join(", ") : "All"
    const departmentNames = query.departmentIds?.length
        ? query.departmentIds.map(id => departments.find(d => d.id === id)?.description ?? id).join(", ")
        : "All"
    const metadata = `Fiscal Year: ${fiscalYear} | Branch: ${branchName} | Project Managers: ${pmNames} | Department: ${departmentNames}`

    const periods = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    const periodHeaders = periods.map(p => calendar.fiscalToLabel(fiscalYear, p))
    const headers = ["Project", "Budget", "Revenue", ...periodHeaders, "Variance"]

    const fmt = (n: number) => n.toFixed(2)
    let grandBudget = 0, grandRevenue = 0, grandVariance = 0
    const grandPeriods = new Array(12).fill(0)

    const dataRows = projects.map(project => {
        const periodValues = periods.map(p => {
            const pf = project.projectFinances.find(f => f.fiscalPeriod === fiscalYear * 100 + p)
            return Number(pf?.forecastAmount ?? 0)
        })
        const totalForecast = project.projectFinances.reduce((sum, f) => sum + Number(f.forecastAmount ?? 0), 0)
        const variance = project.budget - totalForecast

        grandBudget += project.budget ?? 0
        grandRevenue += project.revenue ?? 0
        periodValues.forEach((v, i) => grandPeriods[i] += v)
        grandVariance += variance

        return [
            project.code ?? "",
            fmt(project.budget ?? 0),
            fmt(project.revenue ?? 0),
            ...periodValues.map(fmt),
            fmt(variance),
        ]
    })

    dataRows.push([
        "Grand Total",
        fmt(grandBudget),
        fmt(grandRevenue),
        ...grandPeriods.map(fmt),
        fmt(grandVariance),
    ])

    const csv = [[metadata], [], headers, ...dataRows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `project-forecast-${fiscalYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
};

